"use client";

import { useLiveSession, type LiveStatus } from "@/contexts/live-session-context";
import { LiveIcon, LoaderIcon, ParticipantsIcon } from "../ui/atoms/icons";

/**
 * L'indicateur du live en cours, et la poignée de son panneau.
 *
 * # Il n'ouvre plus rien
 *
 * C'était « Passer en live » : un clic ouvrait une séance. Depuis le
 * 2026-09-14, le live, c'est le récital, et un récital se programme et se
 * commence depuis sa salle d'attente — pas depuis la barre du studio, où
 * rien ne dit à qui l'on joue ni quand. Le bouton ne se montre donc que
 * pendant un live : en ouverture, en connexion, en direct ou en reconnexion.
 * Hors live, il n'y a rien à indiquer, et une place vide vaut mieux qu'un
 * bouton qui ne mène nulle part.
 *
 * Il ne connaît rien de LiveKit ni du protocole : il lit un état. Il se retire
 * de lui-même hors du fournisseur — `useLiveSession` rend `null` plutôt que de
 * lever.
 *
 * # « En live » veut dire que la porte est ouverte
 *
 * Il l'a longtemps dit dès la connexion, « REC » compris, alors que
 * l'interprète était encore seul sur scène à cadrer sa caméra : deux mots faux
 * à côté d'un widget qui dit « rien n'est enregistré et personne n'est encore
 * entré » (2026-09-20). Ce qu'il lit maintenant, c'est `doorsOpen` — nul hors
 * récital, où une séance jointe **est** en direct, et faux pendant le sas.
 *
 * # Le panneau n'est pas le sien
 *
 * Le détail du live vit dans le bandeau de droite (`LiveSessionPanel`), qui
 * partage son logement avec la génération d'exercice — d'où les deux
 * propriétés : l'état d'ouverture appartient à `StudioPage`, seul endroit qui
 * puisse tenir l'exclusion entre les deux panneaux. Un clic l'ouvre, un autre
 * le ferme, y compris pendant la connexion : le panneau dit déjà où l'on en
 * est.
 */
export function GoLiveButton({
  panelOpen,
  onTogglePanel,
}: {
  panelOpen: boolean;
  onTogglePanel: (open: boolean) => void;
}) {
  const live = useLiveSession();

  // Un récital seulement. Une séance à plusieurs n'a ni indicateur ni bandeau :
  // son picto dit qui est là, et tout ce que le bandeau montrait est sur le
  // rouleau (`jam-widget.tsx`, décidé le 2026-09-23). Tant que le billet n'est
  // pas là, on ne sait pas ce qu'on rejoint : rien plutôt qu'un mot à reprendre.
  if (live === null || !isShown(live.status) || live.session?.recital == null) {
    return null;
  }

  const busy = live.status === "opening" || live.status === "connecting";
  // Sans public, ce n'est pas un live : c'est un enregistrement.
  const recordingOnly = live.session?.recital?.recordingOnly === true;
  // Le sas d'un récital : la salle est jointe, la porte ne l'est pas.
  const backstage = live.doorsOpen === false;
  const onAir = isOnAir(live.status) && !backstage;

  return (
    <button
      type="button"
      onClick={() => onTogglePanel(!panelOpen)}
      aria-haspopup="dialog"
      aria-expanded={panelOpen}
      title={
        recordingOnly
          ? "Enregistrement en cours : ouvrir le panneau"
          : backstage
            ? "Sur scène, portes fermées : ouvrir le panneau"
            : "Récital en direct : ouvrir le panneau"
      }
      /* Même gabarit que le bouton de tempo voisin : 28 px de haut, cible
         étendue au doigt par un pseudo-élément débordant. */
      className={`relative inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-[12px] font-medium tracking-wide transition-colors duration-150 cursor-pointer pointer-coarse:after:absolute pointer-coarse:after:content-[''] pointer-coarse:after:-inset-y-2 pointer-coarse:after:inset-x-0 ${
        onAir
          ? "text-red-300 bg-red-500/10 hover:bg-red-500/15"
          : "text-text-secondary hover:text-text-primary hover:bg-white/[0.04]"
      }`}
    >
      {busy ? (
        <LoaderIcon size={13} className="animate-spin" />
      ) : recordingOnly ? (
        /* La pastille bat quand quelque chose est capté. Dans le sas, elle est
           là sans battre : il n'y a rien à annoncer. */
        <span
          aria-hidden
          className={`size-2 rounded-full ${onAir ? "animate-pulse bg-red-500" : "bg-white/25"}`}
        />
      ) : (
        <LiveIcon size={13} />
      )}
      <span>{labelFor(live.status, recordingOnly, backstage)}</span>

      {/* L'interprète voit, sans ouvrir le panneau, que son récital
          s'enregistre. `recording` est nul chez un spectateur. */}
      {onAir && live.recording === "on" && !recordingOnly && (
        <span className="inline-flex items-center gap-1 font-mono text-[10px] tracking-[0.08em] text-red-300">
          <span aria-hidden className="size-1.5 animate-pulse rounded-full bg-red-500" />
          REC
          <span className="sr-only">Enregistrement en cours</span>
        </span>
      )}

      {/* Les autres, sans soi, comme ce bouton les a toujours comptés — mais
          lus dans l'audience du service : LiveKit ne montre plus les
          spectateurs d'un récital. */}
      {onAir && !recordingOnly && live.headcount > 1 && (
        <span className="inline-flex items-center gap-1 text-text-muted">
          <ParticipantsIcon size={12} />
          <span className="tabular-nums">{live.headcount - 1}</span>
        </span>
      )}
    </button>
  );
}

/**
 * Le motif d'une jointure ratée, là où l'indicateur aurait été.
 *
 * L'erreur ne se lisait que dans le panneau, et le panneau ne s'ouvrait que
 * par le bouton. Le bouton se retirant hors live, une salle refusée — récital
 * terminé, place non réservée, service absent — aurait laissé le studio sans
 * rien dire. La pastille dit le motif, et la croix l'efface en quittant pour
 * de bon (`leave` oublie aussi l'adresse du récital).
 */
export function LiveJoinError() {
  const live = useLiveSession();

  if (live === null || live.status !== "error" || live.error === null) {
    return null;
  }

  return (
    <span
      role="alert"
      className="inline-flex items-center gap-1.5 h-7 max-w-[280px] pl-2.5 pr-1 rounded-md text-[12px] text-red-300 bg-red-500/10"
    >
      <LiveIcon size={13} className="shrink-0" />
      <span className="truncate" title={live.error}>
        {live.error}
      </span>
      <button
        type="button"
        onClick={() => void live.leave()}
        aria-label="Fermer le message du live"
        className="w-5 h-5 shrink-0 grid place-items-center rounded hover:bg-red-500/15 cursor-pointer"
      >
        <svg
          width="10"
          height="10"
          viewBox="0 0 14 14"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          aria-hidden="true"
        >
          <path d="M4 4l6 6M10 4l-6 6" />
        </svg>
      </button>
    </span>
  );
}

function isShown(status: LiveStatus): boolean {
  return status === "opening" || status === "connecting" || isOnAir(status);
}

function isOnAir(status: LiveStatus): boolean {
  return status === "live" || status === "reconnecting";
}

function labelFor(status: LiveStatus, recordingOnly: boolean, backstage: boolean): string {
  if (status === "reconnecting") {
    return "Reconnexion…";
  }

  if (status === "live") {
    // Le même mot que le widget du rouleau et que sa porte de sortie
    // (« Quitter la scène ») : on y est, personne ne regarde encore.
    if (backstage) {
      return recordingOnly ? "En attente" : "Sur scène";
    }

    return recordingOnly ? "Enregistrement" : "En live";
  }

  return "Connexion…";
}
